import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';

interface ChecklistItem {
  id: string;
  label: string;
  type: 'checkbox' | 'yes_no' | 'select' | 'text';
  options?: string[];
  placeholder?: string;
}

interface ChecklistSectionProps {
  title: string;
  items: ChecklistItem[];
  answers: Record<string, any>;
  onAnswerChange: (itemId: string, value: any) => void;
  className?: string;
}

export const ChecklistSection: React.FC<ChecklistSectionProps> = ({
  title,
  items,
  answers,
  onAnswerChange,
  className = ''
}) => {
  const renderItem = (item: ChecklistItem) => {
    switch (item.type) {
      case 'checkbox':
        return (
          <div key={item.id} className="flex items-center space-x-2 py-2">
            <input
              type="checkbox"
              id={item.id}
              checked={answers[item.id] === true}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onAnswerChange(item.id, e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor={item.id} className="text-sm font-normal cursor-pointer">
              {item.label}
            </label>
          </div>
        );

      case 'yes_no':
        return (
          <div key={item.id} className="py-3">
            <label className="text-sm font-medium mb-2 block">{item.label}</label>
            <div className="flex items-center gap-6">
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  value="Yes"
                  id={`${item.id}-yes`}
                  checked={answers[item.id] === 'Yes'}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onAnswerChange(item.id, e.target.value)}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor={`${item.id}-yes`} className="font-normal cursor-pointer">Yes</label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  value="No"
                  id={`${item.id}-no`}
                  checked={answers[item.id] === 'No'}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onAnswerChange(item.id, e.target.value)}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor={`${item.id}-no`} className="font-normal cursor-pointer">No</label>
              </div>
            </div>
          </div>
        );

      case 'select':
        return (
          <div key={item.id} className="py-3">
            <label className="text-sm font-medium mb-2 block">{item.label}</label>
            <div className="flex flex-wrap gap-4">
              {item.options?.map((option) => (
                <div key={option} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    value={option}
                    id={`${item.id}-${option}`}
                    checked={answers[item.id] === option}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onAnswerChange(item.id, e.target.value)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor={`${item.id}-${option}`} className="font-normal cursor-pointer whitespace-nowrap">
                    {option}
                  </label>
                </div>
              ))}
            </div>
          </div>
        );

      case 'text':
        return (
          <div key={item.id} className="py-3">
            <label htmlFor={item.id} className="text-sm font-medium mb-2 block">
              {item.label}
            </label>
            <textarea
              id={item.id}
              placeholder={item.placeholder || ''}
              value={answers[item.id] || ''}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onAnswerChange(item.id, e.target.value)}
              className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {items.map((item) => renderItem(item))}
        </div>
      </CardContent>
    </Card>
  );
};
